import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  ListArPaymentApplicationsInput,
  ListArPaymentApplicationsResponse,
} from '@erp/rpc';

export function useArPaymentApplicationsList(
  filters: ListArPaymentApplicationsInput = {},
) {
  const query = useQuery(
    orpc.accountsReceivable.arPaymentApplications.list.queryOptions({
      input: filters,
    }),
  );

  const paymentApplications = query.data?.paymentApplications ?? [];
  const pagination = query.data?.pagination;

  return {
    ...query,
    paymentApplications,
    pagination,
  } satisfies typeof query & {
    data?: ListArPaymentApplicationsResponse;
    paymentApplications: ListArPaymentApplicationsResponse['paymentApplications'];
    pagination: ListArPaymentApplicationsResponse['pagination'] | undefined;
  };
}

function invalidateAfterAllocationChange(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  // Payment detail shows the allocation list; the allocation list endpoint and
  // invoice balances/aging all shift when an allocation is added or removed.
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arPayments.get.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arPaymentApplications.list.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arInvoices.list.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arInvoices.get.key(),
  });
}

/** Manually allocate a payment to an invoice. */
export function useCreateArPaymentApplication() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arPaymentApplications.create.mutationOptions({
      onSuccess: () => invalidateAfterAllocationChange(queryClient),
    }),
  );
}

/** Remove a manually-created allocation. */
export function useDeleteArPaymentApplication() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arPaymentApplications.delete.mutationOptions({
      onSuccess: () => invalidateAfterAllocationChange(queryClient),
    }),
  );
}
